import { useEffect, useState } from "react";
import axios from "axios";
import { Disc3, ExternalLink, Loader2, Play, ListMusic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AlbumFlipCards = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState({});
  const [playerOpen, setPlayerOpen] = useState(null); // album currently playing in modal

  useEffect(() => {
    axios
      .get(`${API}/albums`)
      .then((r) => setAlbums(r.data))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <section
      id="music"
      data-testid="music-section"
      className="relative py-24 md:py-32 bg-[#0a0a0a] grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative">
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
              The Catalogue
            </span>
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Albums. <span className="text-white/40">Flip. Press play.</span>
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Each card is an era. The older joints came before I fully gave my
            life to Christ — the newer work is Gospel. It's a journey, all of
            it, and God has been faithful through every chapter.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/50">
            <Loader2 size={16} className="animate-spin" /> Loading catalogue…
          </div>
        ) : albums.length === 0 ? (
          <p className="text-white/50">No albums yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {albums.map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                isFlipped={flipped[album.id]}
                onTap={() => toggle(album.id)}
                onPlay={() => setPlayerOpen(album)}
              />
            ))}
          </div>
        )}
      </div>

      {playerOpen && (
        <AlbumPlayerModal album={playerOpen} onClose={() => setPlayerOpen(null)} />
      )}
    </section>
  );
};

const AlbumCard = ({ album, isFlipped, onTap, onPlay }) => {
  const hasTracks = album.tracks && album.tracks.length > 0;
  return (
    <div
      data-testid={`album-flip-card-${album.id}`}
      className="perspective-1200 group"
    >
      <div
        className={`flip-card relative aspect-[3/4] w-full ${
          isFlipped ? "is-flipped" : ""
        }`}
      >
        <div className="flip-card-inner relative w-full h-full">
          {/* Front — clickable to flip */}
          <div
            className="flip-face border border-white/10 overflow-hidden bg-[#111] cursor-pointer"
            onClick={onTap}
          >
            {album.cover ? (
              <img
                src={album.cover}
                alt={album.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Disc3 size={80} className="text-white/20" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
              {album.era && (
                <span className="text-[10px] uppercase tracking-[0.3em] px-2 py-1 border border-white/30 text-white/80 backdrop-blur-sm">
                  {album.era}
                </span>
              )}
              {album.year && (
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                  {album.year}
                </span>
              )}
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight">
                {album.title}
              </h3>
              <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                <Disc3 size={12} />
                Flip for details
              </div>
            </div>
          </div>

          {/* Back — NOT flipping on click; only specific buttons act */}
          <div className="flip-face flip-back bg-[#0f0f0f] border border-[#D4AF37]/40 gold-glow p-6 flex flex-col">
            <button
              data-testid={`album-${album.id}-back-to-front`}
              onClick={onTap}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white text-lg"
              title="Flip back"
            >
              ×
            </button>

            <div className="flex items-start justify-between mb-4 pr-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
                  {album.year} {album.era && `· ${album.era}`}
                </div>
                <h3 className="font-display font-bold text-xl leading-tight">
                  {album.title}
                </h3>
              </div>
              <Disc3 size={22} className="text-[#D4AF37]" />
            </div>

            {hasTracks ? (
              <ol className="flex-1 space-y-2 overflow-y-auto">
                {album.tracks.map((t, i) => (
                  <li
                    key={i}
                    data-testid={`album-${album.id}-track-${i}`}
                    className="flex items-baseline gap-3 py-2 border-b border-white/5"
                  >
                    <span className="font-display text-xs text-[#D4AF37] w-6 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-white/85 flex-1">{t}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                <ListMusic size={40} className="text-[#D4AF37]/40 mb-4" />
                <p className="text-sm text-white/60 leading-relaxed">
                  Tap <span className="text-[#D4AF37]">Play Album</span> to
                  stream the full project right here without leaving the site.
                </p>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              {album.embed_url && (
                <button
                  data-testid={`album-${album.id}-play`}
                  onClick={onPlay}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-10 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F]"
                >
                  <Play size={12} /> Play Album
                </button>
              )}
              {album.bandlab_url && (
                <a
                  data-testid={`album-${album.id}-link`}
                  href={album.bandlab_url}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center justify-center gap-2 h-10 ${
                    album.embed_url ? "px-4" : "flex-1"
                  } border border-white/20 text-white text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37]`}
                  title="Open on BandLab"
                >
                  <ExternalLink size={12} />
                  {!album.embed_url && "Listen on BandLab"}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AlbumPlayerModal = ({ album, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="album-player-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-[#0e0e0e] border border-[#D4AF37]/40 gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
              Now Playing
            </div>
            <div className="font-display text-lg font-semibold mt-1">
              {album.title}
            </div>
          </div>
          <button
            data-testid="album-player-close"
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <iframe
          title={album.title}
          src={album.embed_url}
          width="100%"
          height="440"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          style={{ display: "block", background: "#000" }}
        />
        {album.bandlab_url && (
          <div className="px-6 py-3 border-t border-white/10 text-center">
            <a
              href={album.bandlab_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#D4AF37] inline-flex items-center gap-2"
            >
              Open full album on BandLab <ExternalLink size={12} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
